import {Injectable} from '@angular/core';
import {SeriesSummaryChartData, SeriesSummaryChartService} from "./series-summary-chart.service";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {ApiService} from "../../services/api/api.service";
import {SeriesDataService} from "../../services/series_data.service";
import {ChartConfigTranslationService} from "../../services/chart-config-translation.service";
import {FormatNumberPipe} from "../../shared/pipes";
import {
    GenericChartTileConfiguration,
    SeriesSummaryChartTileConfiguration
} from "../chart-config/generic-chart-tile.configuration";
import {SeriesSummary} from "../../_models/api/series-summary";
import {Subject} from "rxjs";
import {KeyMap} from "../../_typing/generic-types";
import {SeriesSummaryChartConfiguration} from "../chart-config/chart-configuration";
import {Shape} from "plotly.js-dist-min";
import {NotificationService} from "../../services/notification.service";
import {over} from "lodash-es";
import {moment_timezone as moment} from "../../services/timezone-selector.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {DateTimePeriodService} from "../../services/date-time-period.service";

@Injectable({
    providedIn: 'root'
})
export class ProgressTrackerChartService {

    private $chartConfigReadySubject: Subject<any> = new Subject();
    $chartConfigReady = this.$chartConfigReadySubject.asObservable();

    maxTotal = 100;

    constructor(private dateInst: DateTimeInstanceService,
                private dateTimePeriodService: DateTimePeriodService,
                private api: ApiService,
                private seriesData: SeriesDataService,
                private chartConfigService: ChartConfigTranslationService,
                private formatNumber: FormatNumberPipe,
                private ssChartService: SeriesSummaryChartService,
                private notification: NotificationService) {
    }

    getChart(tile_config: SeriesSummaryChartTileConfiguration) {
        this.getChartData(tile_config);
    }

    private getChartData(tileConfig: SeriesSummaryChartTileConfiguration) {
        this.ssChartService.getChart(tileConfig).subscribe((data: SeriesSummaryChartData) => {
            this.configureChartConfig(tileConfig, data)
        })
    }

    configureChartConfig(tileConfig: SeriesSummaryChartTileConfiguration, data: SeriesSummaryChartData) {
        let chartConfig: SeriesSummaryChartConfiguration = data.chartConfig;
        chartConfig.barmode = 'stack';

        // Reset maxTotal each time we recalculate the chart
        this.maxTotal = 100;

        // Calculate values and determine over/under budget status
        const result = this._getYValuesAsPercentWithOrdering(tileConfig, data.seriesSummaryData);
        chartConfig.y_values = result.yValues;
        chartConfig.key_values = result.orderedColumns;

        chartConfig.text_values = this._getTextValues(tileConfig, data.seriesSummaryData, result.orderedColumns);
        chartConfig.x_values = this._getSeriesText(tileConfig, chartConfig.x_values)
        chartConfig.today_line = this._getTodayLine(tileConfig.period_type);
        chartConfig.y_max = Math.ceil(this.maxTotal);
        chartConfig.y_min = 0;

        // Set colors based on ordered columns
        chartConfig.colours = this._getDefaultColorsForColumns(tileConfig, result.orderedColumns);

        // Set column names based on ordered columns
        chartConfig.names = this._getColumnNamesForColumns(tileConfig, result.orderedColumns, chartConfig.names);

        const plot = this.chartConfigService.getConfiguredSeriesSummaryChart('plotly', chartConfig);
        this.$chartConfigReadySubject.next(plot);
    }

    private _getSeriesText(tileConfig: SeriesSummaryChartTileConfiguration, xValues) {
        tileConfig.series_list.forEach((series, index) => {
            if (series.custom_legend_name) {
                xValues.series[index] = series.custom_legend_name;
            }
        })
        return xValues;
    }

    private _getYValuesAsPercentWithOrdering(tileConfig: SeriesSummaryChartTileConfiguration, seriesSummaryData: SeriesSummary[]): {yValues: KeyMap<string[]>, orderedColumns: string[]} {
        let abbr = this._getPeriodShortName(tileConfig);
        let daily = 'Daily';
        let fullPeriod = 'Full @P @E'.replace('@E', tileConfig.estimate_type).replace('@P', tileConfig.period_type);
        let fullPeriodVariance = fullPeriod + ' Variance';
        let errors = [];

        let ssPercentages = [];

        for (let d of seriesSummaryData) {
            let series = {};
            let budget = d[fullPeriod] || 0; // The full period budget/estimate
            let cumulativeTotal = d[abbr] || 0; // Total cumulative including daily
            let yesterday = d[daily] || 0;
            let cumulativeWithoutYesterday = cumulativeTotal - yesterday;

            if (!budget) {
                series[abbr] = 0;
                series[daily] = 0;
                series[fullPeriod] = 0;
                series[fullPeriodVariance] = 0;
                errors.push(`${fullPeriod} data for ${d.Description || d.Name} is incomplete.`);
                ssPercentages.push(series);
                continue;
            }

            // Check if this series is over budget
            const isOverBudgetValue = cumulativeTotal > budget;

            // Calculate percentages based on budget
            const yesterdayPercent = (yesterday / budget) * 100;

            if (isOverBudgetValue) {
                // Over budget scenario
                // Green: cumulative without yesterday, capped at 100%
                const greenPercent = Math.min((cumulativeWithoutYesterday / budget) * 100, 100);
                // Blue: overage excluding yesterday
                const overagePercent = Math.max(0, ((cumulativeWithoutYesterday - budget) / budget) * 100);

                series[abbr] = greenPercent;
                series[fullPeriodVariance] = overagePercent;
                series[daily] = yesterdayPercent;
                series[fullPeriod] = 0; // No remaining when over budget
                this.maxTotal = Math.max(this.maxTotal, greenPercent + overagePercent + yesterdayPercent);
            } else {
                // Under budget scenario
                const cumulativeWithoutYesterdayPercent = (cumulativeWithoutYesterday / budget) * 100;
                const remainingPercent = 100 - (cumulativeTotal / budget) * 100;

                series[abbr] = cumulativeWithoutYesterdayPercent;
                series[daily] = yesterdayPercent;
                series[fullPeriod] = remainingPercent;
                series[fullPeriodVariance] = 0; // No over budget when under budget
            }

            ssPercentages.push(series);
        }

        if (errors.length) {
            this.notification.openError(errors.join(', '));
        }

        // Always include all 4 columns - individual series values will be 0 where not applicable
        const orderedColumns: string[] = [abbr, fullPeriodVariance, daily, fullPeriod];

        return {
            yValues: this.getYValues(tileConfig, ssPercentages),
            orderedColumns: orderedColumns
        };
    }

    private getYValues(tileConfig: SeriesSummaryChartTileConfiguration, seriesSummaryData: Partial<SeriesSummary>[]): KeyMap<string[]> {
        let columnDataMap: KeyMap<string[]> = {};
        tileConfig.columns.forEach(column => {
            columnDataMap[column] = seriesSummaryData.map(d => String(d[column]))
        })
        return columnDataMap;
    }

    private _getTextValues(tileConfig: SeriesSummaryChartTileConfiguration, seriesSummaryData: Partial<SeriesSummary>[], orderedColumns: string[]): KeyMap<string[]> {
        let columnDataMap: KeyMap<string[]> = {};
        // Get the column names
        const abbr = this._getPeriodShortName(tileConfig);
        const fullPeriod = `Full ${tileConfig.period_type} ${tileConfig.estimate_type}`;
        const fullPeriodVariance = `${fullPeriod} Variance`;

        orderedColumns.forEach(column => {
            columnDataMap[column] = seriesSummaryData.map(d => {
                const budget = d[fullPeriod] || 0;
                const cumulativeTotal = d[abbr] || 0;
                const yesterday = d['Daily'] || 0;
                const cumulativeWithoutYesterday = cumulativeTotal - yesterday;
                const decimalPlaces = tileConfig.column_formats?.[column]?.decimals ?? (d.DecimalPlaces || 0);

                let value = 0;
                if (column === abbr) {
                    // Green bar: cumulative without yesterday, capped at budget
                    if (cumulativeTotal > budget) {
                        // Over budget: show the portion up to budget
                        value = Math.min(cumulativeWithoutYesterday, budget);
                    } else {
                        // Under budget: show cumulative without yesterday
                        value = cumulativeWithoutYesterday;
                    }
                } else if (column === 'Daily') {
                    // Orange bar: yesterday
                    value = yesterday;
                } else if (column === fullPeriod) {
                    // Yellow bar: remaining (only when under budget)
                    if (cumulativeTotal <= budget) {
                        value = budget - cumulativeTotal;
                    } else {
                        value = 0;
                    }
                } else if (column === fullPeriodVariance) {
                    // Blue bar: overage excluding yesterday (only when over budget)
                    if (cumulativeTotal > budget) {
                        value = Math.max(0, cumulativeWithoutYesterday - budget);
                    } else {
                        value = 0;
                    }
                }

                let textValue = value ? value.toFixed(decimalPlaces) : '';

                // Apply HTML formatting for bold and font-size
                if (textValue && tileConfig.column_formats?.[column]) {
                    const format = tileConfig.column_formats[column];
                    if (format.bold) {
                        textValue = `<b>${textValue}</b>`;
                    }
                }

                return textValue;
            });
        });

        return columnDataMap;
    }

    private _getPeriodShortName(tileConfig: SeriesSummaryChartTileConfiguration): string {
        return tileConfig.period_type === 'Week' ? 'WTD' :
            tileConfig.period_type === 'Month' ? 'MTD' :
                tileConfig.period_type === 'Quarter' ? 'QTD' : 'YTD';

    }

    getSelectedCols(tileConfig: SeriesSummaryChartTileConfiguration): string[] {
        let selectedCols = ['Daily', 'Full @P @E', 'Full @P @E Variance'].map(col => col.replace('@E', tileConfig.estimate_type).replace('@P', tileConfig.period_type));
        let abbr = this._getPeriodShortName(tileConfig);
        selectedCols = [abbr].concat((selectedCols.map(col => col.replace('@A', abbr))));
        return selectedCols;
    }

    private _getDefaultColorsForColumns(tileConfig: SeriesSummaryChartTileConfiguration, orderedColumns: string[]): KeyMap<string> {
        const colors: KeyMap<string> = {};
        const abbr = this._getPeriodShortName(tileConfig);
        const fullPeriod = `Full ${tileConfig.period_type} ${tileConfig.estimate_type}`;
        const fullPeriodVariance = `${fullPeriod} Variance`;

        orderedColumns.forEach(col => {
            // Check for custom color first, then fall back to defaults
            if (tileConfig.column_formats?.[col]?.background_colour) {
                colors[col] = tileConfig.column_formats[col].background_colour;
            } else if (col === abbr) {
                // Green for cumulative progress
                colors[col] = '#5cb85c';
            } else if (col === 'Daily') {
                // Orange for daily/yesterday
                colors[col] = '#f0ad4e';
            } else if (col === fullPeriod) {
                // Yellow for remaining
                colors[col] = '#f9c74f';
            } else if (col === fullPeriodVariance) {
                // Blue for over budget
                colors[col] = '#5bc0de';
            }
        });

        return colors;
    }

    private _getColumnNamesForColumns(tileConfig: SeriesSummaryChartTileConfiguration, orderedColumns: string[], existingNames: KeyMap<string>): KeyMap<string> {
        const names: KeyMap<string> = existingNames || {};
        const abbr = this._getPeriodShortName(tileConfig);
        const fullPeriod = `Full ${tileConfig.period_type} ${tileConfig.estimate_type}`;
        const fullPeriodVariance = `${fullPeriod} Variance`;

        orderedColumns.forEach(col => {
            if (!names[col]) {
                if (col === abbr) {
                    names[col] = 'Cumulative';
                } else if (col === 'Daily') {
                    names[col] = 'Yesterday';
                } else if (col === fullPeriod) {
                    names[col] = 'Remaining';
                } else if (col === fullPeriodVariance) {
                    names[col] = 'Over Budget';
                }
            }

            // Apply HTML formatting for bold to legend entries
            if (names[col] && tileConfig.column_formats?.[col]?.bold) {
                names[col] = `<b>${names[col]}</b>`;
            }
        });

        return names;
    }


    private _getTodayLine(
        periodType: string,
    ): Partial<Shape> {
        const clamp = (v: number, min = 0, max = 100) => Math.max(min, Math.min(max, v));
        const dtp = this.dateInst.dtp;
        const startHours = this.dateTimePeriodService.defaultStartHour;
        const timezone = this.dateTimePeriodService.selected_timezone;

        // Get the calendar object to access custom period definitions
        const calendar = this.dateTimePeriodService.calendar_dict[dtp.calendar];

        // Target is where the DTP end falls
        const target = moment.tz(dtp.end, timezone)
            .startOf('day')
            .add(startHours, 'hours');

        let periodStart: moment.Moment;
        let periodEnd: moment.Moment;

        // Find the calendar period that contains the target date
        if (calendar?.calendar_periods) {
            // Convert periodType to match CustomTimePeriodRange ('Week' -> 'week')
            const rangeType = periodType.toLowerCase() as 'week' | 'month' | 'quarter' | 'year';

            // Filter periods by type
            const periods = calendar.calendar_periods.filter(p => p.attributes.range === rangeType);

            // Find the period containing the target date
            const currentPeriod = periods.find(p => {
                const startMoment = moment.tz(p.attributes.start, timezone);
                const endMoment = moment.tz(p.attributes.end, timezone);
                return target.isSameOrAfter(startMoment) && target.isBefore(endMoment);
            });

            if (currentPeriod) {
                periodStart = moment.tz(currentPeriod.attributes.start, timezone)
                    .startOf('day')
                    .add(startHours, 'hours');
                periodEnd = moment.tz(currentPeriod.attributes.end, timezone)
                    .startOf('day')
                    .add(startHours, 'hours');
            }
        }

        // Fallback to standard calendar calculations if no custom period found
        if (!periodStart || !periodEnd) {
            switch (periodType) {
                case 'Week': {
                    const startOfWeek = target.clone().startOf('week');
                    periodStart = startOfWeek.clone().add(startHours, 'hours');
                    periodEnd = periodStart.clone().add(1, 'week');
                    break;
                }
                case 'Quarter': {
                    const quarter = Math.floor(target.month() / 3);
                    periodStart = target.clone().month(quarter * 3).startOf('month').add(startHours, 'hours');
                    periodEnd = periodStart.clone().add(3, 'months');
                    break;
                }
                case 'Year': {
                    periodStart = target.clone().startOf('year').add(startHours, 'hours');
                    periodEnd = periodStart.clone().add(1, 'year');
                    break;
                }
                case 'Month':
                default: {
                    periodStart = target.clone().startOf('month').add(startHours, 'hours');
                    periodEnd = periodStart.clone().add(1, 'month');
                    break;
                }
            }
        }

        const totalMs = periodEnd.diff(periodStart);
        const elapsedMs = target.diff(periodStart);
        const percent = clamp((elapsedMs / totalMs) * 100);

        if (!percent) return;
        return {
            type: 'line',
            x0: percent,
            x1: percent,
            yref: 'paper',
            y0: 0,
            y1: 1,
            line: {
                color: 'red',
                width: 2,
                dash: 'dash',
            }
        } as Partial<Shape>;
    }
}
